import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import Cta from '@/components/cta';
import { Partners } from '@/components/partners';

export default function CookiesPolicyPage() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);

  type Section = { key: string; title: string };

  const sections: Section[] = [
    { key: 'scopePurpose', title: t('cookies.scopePurpose.title', 'Scope & Purpose') },
    { key: 'eu', title: t('cookies.eu.title', 'Europe (EU / EEA / UK)') },
    { key: 'us', title: t('cookies.us.title', 'United States (Federal + State)') },
    { key: 'mena', title: t('cookies.mena.title', 'MENA (Middle East & North Africa)') },
    { key: 'otherRegions', title: t('cookies.otherRegions.title', 'Other Regions') },
    { key: 'consentManagement', title: t('cookies.consentManagement.title', 'Consent Management') },
    { key: "childrensPrivacy", title: t('cookies.childrensPrivacy.title', "Children's Privacy") },
    { key: 'regionalNotices', title: t('cookies.regionalNotices.title', 'Regional Notices') },
    { key: 'updatesContact', title: t('cookies.updatesContact.title', 'Updates & Contact') },
  ];

  const renderSection = (key: string) => {
    switch (key) {
      case 'scopePurpose': {
        const body = t('cookies.scopePurpose.body', { returnObjects: true }) as string[];
        return (
          <section id="section-scopePurpose">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.scopePurpose.title')}</h2>
            {body?.map((p, i) => (
              <p key={i} className="mb-4">{p}</p>
            ))}
          </section>
        );
      }
      case 'eu': {
        const points = t('cookies.eu.points', { returnObjects: true }) as string[];
        return (
          <section id="section-eu">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.eu.title')}</h2>
            <ul className="list-disc pl-6 space-y-2">
              {points?.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
        );
      }
      case 'us': {
        const intro = t('cookies.us.intro', { returnObjects: true }) as string[];
        const bullets = t('cookies.us.bullets', { returnObjects: true }) as string[];
        return (
          <section id="section-us">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.us.title')}</h2>
            {intro?.map((p, i) => (
              <p key={i} className="mb-3">{p}</p>
            ))}
            <ul className="list-disc pl-6 space-y-2">
              {bullets?.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </section>
        );
      }
      case 'mena': {
        const points = t('cookies.mena.points', { returnObjects: true }) as string[];
        return (
          <section id="section-mena">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.mena.title')}</h2>
            <ul className="list-disc pl-6 space-y-2">
              {points?.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </section>
        );
      }
      case 'otherRegions': {
        const regions = t('cookies.otherRegions.regions', { returnObjects: true }) as { name: string; text: string }[];
        return (
          <section id="section-otherRegions">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.otherRegions.title')}</h2>
            <div className="space-y-4">
              {regions?.map((r, i) => (
                <div key={i}>
                  <h3 className="font-semibold">{r.name}</h3>
                  <p>{r.text}</p>
                </div>
              ))}
            </div>
          </section>
        );
      }
      case 'consentManagement': {
        const bullets = t('cookies.consentManagement.bullets', { returnObjects: true }) as string[];
        return (
          <section id="section-consentManagement">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.consentManagement.title')}</h2>
            <ul className="list-disc pl-6 space-y-2">
              {bullets?.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </section>
        );
      }
      case 'childrensPrivacy': {
        const bullets = t('cookies.childrensPrivacy.bullets', { returnObjects: true }) as string[];
        return (
          <section id="section-childrensPrivacy">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.childrensPrivacy.title')}</h2>
            <ul className="list-disc pl-6 space-y-2">
              {bullets?.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </section>
        );
      }
      case 'regionalNotices': {
        const regions = t('cookies.regionalNotices.regions', { returnObjects: true }) as { name: string; bullets: string[] }[];
        return (
          <section id="section-regionalNotices">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.regionalNotices.title')}</h2>
            <div className="space-y-4">
              {regions?.map((r, i) => (
                <div key={i}>
                  <h3 className="font-semibold">{r.name}</h3>
                  <ul className="list-disc pl-6 space-y-1 mt-1">
                    {r.bullets?.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        );
      }
      case 'updatesContact': {
        const bullets = t('cookies.updatesContact.bullets', { returnObjects: true }) as string[];
        return (
          <section id="section-updatesContact">
            <h2 className="text-2xl font-semibold mb-4">{t('cookies.updatesContact.title')}</h2>
            <ul className="list-disc pl-6 space-y-2">
              {bullets?.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </section>
        );
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <div className="w-full z-50 px-4 py-20 flex flex-col items-center">
        <h1 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-2">
          {t('cookies.pageTitle', 'Oleyes Security – Cookie Policy')}
        </h1>
        <p className="text-center text-sm text-slate-500 dark:text-slate-400">
          {t('cookies.lastUpdated', '')}
        </p>
      </div>
      <div className="min-h-screen pt-1 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 relative">
          {/* Sidebar Navigation */}
          <nav className="md:w-64 w-full md:sticky md:top-24 flex-shrink-0 z-10 md:h-[calc(100vh-6rem)] md:overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            <ul className="flex md:flex-col flex-row overflow-x-auto gap-2 md:gap-3 md:overflow-y-auto md:space-y-1 p-2 md:p-0">
              {sections.map((section, idx) => (
                <li key={section.key} className="md:w-full">
                  <button
                    className={`w-full px-4 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      selected === idx
                        ? 'font-bold text-primary underline'
                        : 'font-medium text-slate-800 dark:text-slate-200 hover:text-primary/80 hover:underline'
                    }`}
                    onClick={() => {
                      setSelected(idx);
                      const sectionEl = document.getElementById(`section-${section.key}`);
                      if (sectionEl) {
                        sectionEl.scrollIntoView({ behavior: 'auto', block: 'start' });
                      }
                    }}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content Section */}
          <main className="flex-1 max-w-3xl mx-auto p-0 sm:p-0 min-h-[60vh] max-h-[80vh] overflow-y-auto">
            <div className="p-4 sm:p-8">
              {renderSection(sections[selected].key)}
            </div>
          </main>
        </div>
      </div>
      <Partners />
      <Cta />
      <Footer />
    </>
  );
}
